// Shared map constants + pure helpers for the dispatch tactical map.
// Split out of DispatchMap.jsx (refactor only — logic unchanged).
import L from "leaflet";

export const CENTER = [39.9526, -75.1652];

// Switchable public-safety basemaps (all free, no API keys).
export const BASEMAPS = {
  dark: { label: "DARK", url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attr: "&copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors" },
  streets: { label: "STREETS", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "&copy; OpenStreetMap contributors" },
  nav: { label: "NAV", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", attr: "&copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors" },
  sat: { label: "SAT", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attr: "&copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics" },
};

export const GLYPH = {
  police: '<path d="M12 2 4 5v6c0 5 8 9 8 9s8-4 8-9V5z"/>',
  fire: '<path d="M12 2c1 3 4 4 4 8a4 4 0 1 1-8 0c0-1 .6-2 .6-2S6 9 6 12a6 6 0 1 0 12 0c0-6-6-8-6-10z"/>',
  ems: '<path d="M10 3h4v5h5v4h-5v5h-4v-5H5V8h5z"/>',
};
export const ACTIVE_UNIT = new Set(["dispatched", "enroute", "onscene", "directed_patrol", "active", "transport"]);

export function valid(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);
}

// GPS freshness — dispatch must never mistake stale data for live position.
export function gpsState(u) {
  if (u.gps?.online === false) return ["OFFLINE", "#6B7280"];
  const ts = u.gps?.ts;
  if (!ts) return ["NO GPS", "#6B7280"];
  const age = (Date.now() - new Date(ts).getTime()) / 1000;
  if (age < 45) return ["LIVE", "#22C55E"];
  if (age < 180) return ["LOCATION DELAYED", "#F59E0B"];
  if (age < 900) return ["LOCATION STALE", "#F97316"];
  return ["LAST KNOWN", "#EF4444"];
}
export function ageLabel(ts) {
  const s = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 90) return `${s} seconds ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${new Date(ts).toLocaleString("en-GB", { hour12: false })}`;
}

export function unitIcon(color, type, active) {
  const g = GLYPH[type] || '<circle cx="12" cy="12" r="6"/>';
  return L.divIcon({
    className: "",
    html: `<div class="map-unit${active ? " map-active" : ""}" style="--c:${color}">
      <span class="map-ring"></span>
      <span class="map-glyph" style="background:${color}">
        <svg viewBox="0 0 24 24" fill="#fff" width="14" height="14">${g}</svg>
      </span></div>`,
    iconSize: [42, 42], iconAnchor: [21, 21],
  });
}

export function incidentIcon(color, critical) {
  return L.divIcon({
    className: "",
    html: `<div class="map-inc${critical ? " map-active" : ""}" style="--c:${color}">
      <span class="map-ring"></span>
      <span class="map-diamond" style="background:${color}"></span></div>`,
    iconSize: [40, 40], iconAnchor: [20, 20],
  });
}

// Reject far-flung outliers so a stray coord can't zoom the map out to a world view.
export function coreFit(points) {
  if (points.length <= 1) return points;
  const lats = points.map((p) => p[0]).sort((a, b) => a - b);
  const lngs = points.map((p) => p[1]).sort((a, b) => a - b);
  const mlat = lats[Math.floor(lats.length / 2)];
  const mlng = lngs[Math.floor(lngs.length / 2)];
  const core = points.filter((p) => Math.abs(p[0] - mlat) <= 1.5 && Math.abs(p[1] - mlng) <= 1.5);
  return core.length ? core : points;
}
